import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  EuiPageSideBar,
  EuiSideNav,
  EuiSideNavItemType,
} from "@elastic/eui";
import { useAnketaI18n } from "../../internationalization";
import {
  AnketaActionsEnum,
  AnketaResourcesEnum,
  hasPermission,
  OpsanioFeatureEnum,
} from "../../auth";
import {
  AnketaCatalogs,
  getCatalogSideNavItems,
  sideNavQuestionnaire,
} from "../../questionnaires";
import { useLocation, useNavigate } from "react-router";
import { useApi } from "../../api";
import {
  EvaluationContext,
  useAnketa,
} from "../../questionnaires/components/context_provider";
import { useAnketaAuth } from "../../auth/context";
import { getOpsanioFeaturePermission, getOpsanioPermissions } from "src/opsanio";
import { getMedocinoSupportPermissions } from "src/medocino";

export interface SideNavProps {
  route?: string;
  id?: string;
  params?: string;
}

export const SideNav = (): JSX.Element => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, keycloak, permissionMatrix } = useAnketaAuth();
  const { translate } = useAnketaI18n();
  const { Get } = useApi();
  const ctx = useAnketa();
  const evalCtx = useContext(EvaluationContext);

  const [selectedItemId, setSelectedItemId] = useState<string>("");
  const [questionnaires, setQuestionnaires] = useState<Array<AnketaCatalogs>>(
    []
  );

  const canFetch = useMemo(
    () => Boolean(keycloak?.token && typeof keycloak.token === "string"),
    [keycloak]
  );

  const questionnairePermission = useMemo(
    () =>
      hasPermission(
        user,
        AnketaResourcesEnum.questionnaires,
        AnketaActionsEnum.view,
        undefined,
        permissionMatrix
      ),
    [user, permissionMatrix]
  );

  const opsanioPermissions = useMemo(
    () => getOpsanioPermissions(user, permissionMatrix),
    [user, permissionMatrix]
  );
  const medocinoSupportPermissions = useMemo(
    () => getMedocinoSupportPermissions(user),
    [user]
  );

  const selectItem = ({ route, id }: SideNavProps) => {
    if (route && route.trim() !== "") {
      navigate(`/${AnketaResourcesEnum.questionnaires}${route}`);
    }
    if (id) setSelectedItemId(id);
  };

  const questionnaireItem = useMemo(() => {
    const currentQuestionnaireItem = getCatalogSideNavItems(
      ctx.catalog,
      ctx.i18nContext,
      selectItem,
      selectedItemId
    );

    return sideNavQuestionnaire(
      questionnairePermission,
      questionnaires,
      "2",
      selectItem,
      translate,
      evalCtx.focusItem,
      currentQuestionnaireItem
    );
  }, [
    questionnairePermission,
    questionnaires,
    ctx.catalog,
    selectedItemId,
    evalCtx.focusItem,
  ]);

  const [sideNavItems, setSideNavItems] = useState<
    Array<EuiSideNavItemType<any>>
  >([]);

  useEffect(() => {
    const items: Array<EuiSideNavItemType<any>> = [
      {
        id: 7,
        name: "Home",
        items: [
          { id: 8, name: "Über uns", onClick: () => navigate(`/home/#about`) },
          {
            id: 9,
            name: "opsanio",
            onClick: () => navigate(`/home/#appliances`),
          },
          {
            id: 10,
            name: "Services",
            onClick: () => navigate(`/home/#services`),
          },
          {
            id: 11,
            name: "Kontakt",
            onClick: () => navigate(`/home/#contact`),
          },
        ],
      },
    ];

    if (questionnairePermission) {
      items.push({
        id: 2,
        name: translate("anketa.questionnaires"),
        items: questionnaireItem,
      });
    }

    if (opsanioPermissions) {
      const opsanioItems = [];
      if (
        getOpsanioFeaturePermission(
          user,
          OpsanioFeatureEnum.updates,
          permissionMatrix
        )
      ) {
        opsanioItems.push({
          id: 6,
          name: "Updates",
          onClick: () => navigate(`/${AnketaResourcesEnum.opsanio}/update`),
        });
        // Same `updates` feature gate: the version overview is the read-only
        // release-side view of what the update manager promotes.
        opsanioItems.push({
          id: "opsanio-version",
          name: "Versionen",
          onClick: () => navigate(`/${AnketaResourcesEnum.opsanio}/version`),
        });
      }
      if (
        getOpsanioFeaturePermission(
          user,
          OpsanioFeatureEnum.contact,
          permissionMatrix
        )
      ) {
        opsanioItems.push({
          id: 7,
          name: "Contact",
          onClick: () => navigate(`/${AnketaResourcesEnum.opsanio}/contact`),
        });
      }
      if (
        getOpsanioFeaturePermission(
          user,
          OpsanioFeatureEnum.appliance,
          permissionMatrix
        )
      ) {
        opsanioItems.push({
          id: 12,
          name: "Appliance",
          onClick: () => navigate(`/${AnketaResourcesEnum.opsanio}/appliance`),
        });
        // Same `appliance` gate: the wall is a live view of the same fleet.
        opsanioItems.push({
          id: "opsanio-health",
          name: "Health",
          onClick: () => navigate(`/${AnketaResourcesEnum.opsanio}/health`),
        });
      }
      if (
        hasPermission(
          user,
          AnketaResourcesEnum.opsanioOpl,
          AnketaActionsEnum.view,
          undefined,
          permissionMatrix
        )
      ) {
        opsanioItems.push({
          id: "opsanio-opl-open",
          name: translate("anketa.opsanio.opl.overview.nav", {
            defaultMessage: "Open points",
          }),
          onClick: () => navigate(`/${AnketaResourcesEnum.opsanio}/opl`),
        });
      }
      if (
        getOpsanioFeaturePermission(
          user,
          OpsanioFeatureEnum.hardware,
          permissionMatrix
        )
      ) {
        opsanioItems.push({
          id: "opsanio-hardware",
          name: "Hardware",
          onClick: () => navigate(`/${AnketaResourcesEnum.opsanio}/hardware`),
        });
      }
      if (opsanioItems.length > 0) {
        items.push({
          id: 5,
          name: translate(`anketa.${AnketaResourcesEnum.opsanio}`),
          items: opsanioItems,
        });
      }
    }

    if (user?.isSudo) {
      items.push({
        id: 15,
        name: "Administration",
        items: [
          {
            id: 16,
            name: "Berechtigungen & Rollen",
            onClick: () => navigate(`/admin`),
          },
        ],
      });
    }

    /*     if (medocinoSupportPermissions) {
      items.push({
        id: 13,
        name: translate(`anketa.${AnketaResourcesEnum.medocinoSupport}`),
        items: [
          {
            id: 14,
            name: "On-Boarding",
            onClick: () => navigate(`/${AnketaResourcesEnum.medocinoSupport}`),
          },
        ],
      });
    } */

    setSideNavItems(items);
  }, [
    questionnairePermission,
    opsanioPermissions,
    // The matrix loads after the user; entries gated on it (e.g. "Offene
    // Punkte") must appear once it arrives.
    permissionMatrix,
    user?.isSudo,
  ]);

  useEffect(() => {
    if (
      canFetch &&
      questionnairePermission &&
      pathname.startsWith(`/${AnketaResourcesEnum.questionnaires}`)
    ) {
      Get<Array<AnketaCatalogs>>({
        path: `v2/${AnketaResourcesEnum.questionnaires}/catalogs`,
        responseType: "json",
        token: keycloak.token,
      })
        .then((response) => response.data && setQuestionnaires(response.data))
        .catch(console.error);
    }
  }, [canFetch, questionnairePermission, pathname]);

  // No side nav for unauthenticated users - a populated `user` is the app's
  // "logged on" signal (see auth/context/keycloak.tsx).
  if (!user) {
    return <></>;
  }

  return (
    <EuiPageSideBar paddingSize="l" sticky>
      <EuiSideNav
        isOpenOnMobile
        items={[
          {
            name: "Anketa",
            id: 1,
            items: sideNavItems,
          },
        ]}
      />
    </EuiPageSideBar>
  );
};
